export type TourPage =
  | 'overview'
  | 'coverage'
  | 'transactions'
  | 'categories'
  | 'reconciliation'

export interface TourStep {
  target: string
  page: TourPage
  title: string
  body: string
  // Used instead of title and body when the account has reconciliation, so the step names the
  // verdicts the area shows. Without it the area shows none.
  reconciledTitle?: string
  reconciledBody?: string
  isChatStep?: boolean
}

export const TOUR_STEPS: TourStep[] = [
  {
    target: 'case-summary',
    page: 'overview',
    title: 'Your case at a glance',
    body: 'Start here to orient yourself to what the files contain: the kinds of accounts, and where the money went.',
  },
  {
    target: 'statement-coverage',
    page: 'coverage',
    title: 'Statement coverage',
    body: "One row per account, one block per month. Blue means you have that month's statement. Grey is a gap, so track down that month's statement.",
    reconciledBody:
      "Find which months reconcile, and which don't, broken down by account. Click any statement period to see the transactions next to the PDF statement.",
  },
  {
    target: 'account-table',
    page: 'transactions',
    title: 'Accounts',
    body: 'One row per account. Check the period and whether it ties out to the statement.',
  },
  {
    target: 'transaction-filters',
    page: 'transactions',
    title: 'Filters and search',
    body: "Search finds any word in the rows, like a payee's name. Add Filter narrows by amount, date, category or account.",
  },
  {
    target: 'transaction-export',
    page: 'transactions',
    title: 'Export',
    body: 'Download the rows to work on them in Excel.',
  },
  {
    target: 'category-chart',
    page: 'categories',
    title: 'Categories',
    body: 'Where the money went, by category. Click a bar to see that category month by month. Odd or large categories are good places to dig.',
  },
  {
    target: 'cycle-table',
    page: 'reconciliation',
    title: 'Statement cycles',
    body: 'One row per statement cycle: the account, its period and its statement. Open one to check its transactions beside the statement pages.',
    reconciledTitle: 'Reconciliation',
    reconciledBody:
      'One row per statement cycle. It shows whether the printed balances tie out to the transactions, and by how much if not. Open one to see it beside the statement pages.',
  },
  {
    target: 'ask-question',
    page: 'overview',
    title: 'Ask a question',
    body: 'Ask anything about this case, e.g. which payments went to the same person.',
    isChatStep: true,
  },
]

interface TourFeatures {
  isChatEnabled: boolean
  isReconciliationEntitled: boolean
}

export const tourStepsFor = ({
  isChatEnabled,
  isReconciliationEntitled,
}: TourFeatures): TourStep[] =>
  TOUR_STEPS.filter((step) => isChatEnabled || !step.isChatStep).map(
    (step) => ({
      ...step,
      title: (isReconciliationEntitled && step.reconciledTitle) || step.title,
      body: (isReconciliationEntitled && step.reconciledBody) || step.body,
    }),
  )
